import { useCallback, useEffect, useRef } from "react";
import { getCameraConstraints } from "./cameraConstraints";

export function useCameraStreamController({
  videoRef,
  streamRef,
  setVideoReady,
  setCameraAvailable,
  setError,
}) {
  const lightLevelRef = useRef(null);

  const measureFrameBrightness = useCallback((stream) => {
    return new Promise((resolve) => {
      try {
        const track = stream.getVideoTracks()[0];
        if (!track) {
          resolve(null);
          return;
        }
        const vid = document.createElement("video");
        vid.srcObject = stream;
        vid.muted = true;
        vid.playsInline = true;
        vid.onloadeddata = () => {
          try {
            const W = 32;
            const H = 18;
            const canvas = document.createElement("canvas");
            canvas.width = W;
            canvas.height = H;
            const ctx = canvas.getContext("2d", { willReadFrequently: true });
            ctx.drawImage(vid, 0, 0, W, H);
            const px = ctx.getImageData(0, 0, W, H).data;
            let sum = 0;
            for (let i = 0; i < px.length; i += 4) {
              sum += 0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2];
            }
            resolve(sum / (px.length / 4));
          } catch {
            resolve(null);
          }
        };
        vid.onerror = () => resolve(null);
        vid.play().catch(() => resolve(null));
        setTimeout(() => resolve(null), 1500);
      } catch {
        resolve(null);
      }
    });
  }, []);

  const startCamera = useCallback(async () => {
    let stream;
    try {
      const constraints = getCameraConstraints();
      stream = await navigator.mediaDevices.getUserMedia(constraints);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setVideoReady(true);
      }
      streamRef.current = stream;
      lightLevelRef.current = await measureFrameBrightness(stream);
      console.log(`[EIE][LIGHT] measured brightness: ${lightLevelRef.current?.toFixed(1) ?? "n/a"}`);
    } catch {
      setCameraAvailable(false);
      setError("Camera not available");
    }
  }, [measureFrameBrightness, setCameraAvailable, setError, setVideoReady, streamRef, videoRef]);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    };
  }, [streamRef]);

  return {
    lightLevelRef,
    startCamera,
  };
}
